import { getCookie, removeCookie, setCookie } from '#pplx/frontend/cookies/provider.js';
import { useCallback, useMemo, useSyncExternalStore } from 'react';
const PPLX_IS_INCOGNITO_COOKIE = 'pplx.is-incognito';
const listeners = new Set();
let isCookieStoreListenerRegistered = false;
let hasIncognitoLocal = false;
let incognitoLocalSnapshot;
const parseIncognitoCookie = (value) => {
    return value === undefined ? undefined : value === 'true';
};
const readIncognitoCookie = () => {
    return parseIncognitoCookie(getCookie(PPLX_IS_INCOGNITO_COOKIE));
};
const emitChange = () => {
    listeners.forEach((listener) => listener());
};
const updateIncognitoSnapshot = (nextValue) => {
    if (hasIncognitoLocal && incognitoLocalSnapshot === nextValue) {
        return;
    }
    hasIncognitoLocal = true;
    incognitoLocalSnapshot = nextValue;
    emitChange();
};
const getIncognitoSnapshot = () => {
    if (!hasIncognitoLocal) {
        incognitoLocalSnapshot = readIncognitoCookie();
        hasIncognitoLocal = true;
    }
    return incognitoLocalSnapshot;
};
const subscribeToCookieStore = () => {
    if (isCookieStoreListenerRegistered || typeof window === 'undefined' || !window.cookieStore) {
        return;
    }
    const handler = (event) => {
        const deletedCookie = event.deleted.find((cookie) => cookie.name === PPLX_IS_INCOGNITO_COOKIE);
        const changedCookie = event.changed.find((cookie) => cookie.name === PPLX_IS_INCOGNITO_COOKIE);
        if (deletedCookie) {
            updateIncognitoSnapshot(undefined);
            return;
        }
        if (changedCookie) {
            updateIncognitoSnapshot(parseIncognitoCookie(changedCookie.value));
        }
    };
    window.cookieStore.addEventListener('change', handler);
    isCookieStoreListenerRegistered = true;
};
const subscribeToIncognito = (listener) => {
    subscribeToCookieStore();
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};
const setIncognito = (update) => {
    const nextValue = typeof update === 'function' ? update(getIncognitoSnapshot()) : update;
    updateIncognitoSnapshot(nextValue);
    setCookie(PPLX_IS_INCOGNITO_COOKIE, String(nextValue));
};
const toggleIncognito = (defaultIsIncognito = false) => {
    setIncognito((isIncognitoLocal) => !(isIncognitoLocal ?? defaultIsIncognito));
};
export const resetIncognito = () => {
    removeCookie(PPLX_IS_INCOGNITO_COOKIE);
    updateIncognitoSnapshot(undefined);
};
/**
 * Returns the user's local incognito preference from cookie.
 * `isIncognitoLocal` is `undefined` when no preference has been set (cookie doesn't exist),
 * which allows fallback to government default in `useIsIncognito`.
 */
export function useIncognito() {
    const isIncognitoLocal = useSyncExternalStore(subscribeToIncognito, getIncognitoSnapshot, () => undefined);
    const setIsIncognito = useCallback((update) => {
        setIncognito(update);
    }, []);
    const toggleIsIncognito = useCallback((defaultIsIncognito) => {
        toggleIncognito(defaultIsIncognito);
    }, []);
    return useMemo(() => ({
        isIncognitoLocal,
        setIsIncognito,
        toggleIncognito: toggleIsIncognito,
    }), [isIncognitoLocal, setIsIncognito, toggleIsIncognito]);
}
//# sourceMappingURL=useIncognito.js.map